import React, { useEffect } from "react"
import { useAppDispatch, useAppNavigate } from "../../reduxStore/hooks"
import { useLocation } from "react-router"
import {
  errorHandlerUpdated,
  ErrorState,
  initialBannerState,
  selectErrorHandler,
} from "../errors/errorSlice"
import { useSelector } from "react-redux"
import { selectIsUserOnboarded } from "../../domain/Practices/selectors"
import { ariaLiveReset } from "../ariaLive/ariaLiveSlice"

interface NavigationListenerProps {
  children?: React.ReactNode
}

const NavigationListener: React.FC<NavigationListenerProps> = ({
  children,
}) => {
  const navigate = useAppNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { errorBanner, infoBanner, warningBanner } =
    useSelector(selectErrorHandler)
  const isUserOnboarded = useSelector(selectIsUserOnboarded)

  useEffect(() => {
    // reset banners that should close on navigation
    const errorStatePayload: Partial<ErrorState> = {}
    if (!!errorBanner.closeOnNavigation) {
      errorStatePayload.errorBanner = initialBannerState
    }
    if (!!infoBanner.closeOnNavigation) {
      errorStatePayload.infoBanner = initialBannerState
    }
    if (!!warningBanner.closeOnNavigation) {
      errorStatePayload.warningBanner = initialBannerState
    }
    dispatch(errorHandlerUpdated(errorStatePayload))

    // reset aria live region on navigation
    dispatch(ariaLiveReset())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname, location.search])

  useEffect(() => {
    if (isUserOnboarded) {
      return
    }

    // Remove previous history to prevent successive back button clicks to navigate back
    window.history.pushState(null, "", window.location.href)

    const handlePopState = (event: PopStateEvent) => {
      if (isUserOnboarded) {
        window.removeEventListener("popstate", handlePopState)
        return
      }

      // Push the current state to prevent navigation
      window.history.pushState(null, "", window.location.href)

      // Sync navigation with react router
      const lastRoute = `${window.location.pathname}${window.location.search}`
      navigate(lastRoute, { replace: true })
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserOnboarded, location.pathname, location.search])

  return <>{children}</>
}

export default NavigationListener
